import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import { errorMessage, newOptionId } from './estimateApi';
import AutoGrowTextarea from './AutoGrowTextarea';

const MAX_OPTIONS = 4;

/**
 * Manually priced options for work the estimator doesn't calculate (e.g. a
 * metal roof), picked from the options the admin set up in Settings →
 * Estimates. Each one added here is this estimate's own copy: its name and
 * description can be edited for the job, and its price is entered here and
 * never recalculated.
 *
 * `options` is [{ id, templateId, title, description, amount }], saved with
 * the rest of the estimate (Save & View / Send).
 */
const CustomPricingOptions = ({
  api,
  options,
  onChange,
  isDisabled,
  toast,
}) => {
  const [saved, setSaved] = useState([]);
  // Saves run one after another so an older list can't land after a newer one.
  const saveQueue = useRef(Promise.resolve());

  useEffect(() => {
    api
      .get('/api/prices/saved-options')
      .then(setSaved)
      .catch(error => console.error('Could not load saved options', error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Saved right away (like photos and attachments) so an added option isn't
  // lost if the estimate is closed before Save & View or Send.
  const persist = next => {
    saveQueue.current = saveQueue.current
      .then(() => api.post('/api/pdf/custom-options', { customOptions: next }))
      .catch(error =>
        toast({
          title: 'Could not save custom options',
          description: errorMessage(error),
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      );
  };

  // Structural changes (add, remove, use default) save immediately; typing
  // only updates the screen and is saved when the field loses focus.
  const change = next => {
    onChange(next);
    persist(next);
  };

  const update = (id, changes) =>
    onChange(options.map(o => (o.id === id ? { ...o, ...changes } : o)));
  const saveEdits = () => persist(options);

  const add = templateId => {
    const template = saved.find(t => t.id === templateId);
    if (!template) return;
    change([
      ...options,
      {
        id: newOptionId(),
        templateId: template.id,
        title: template.name,
        description: template.description,
        amount: '',
      },
    ]);
  };

  const templateFor = option => saved.find(t => t.id === option.templateId);
  const isEdited = option => {
    const template = templateFor(option);
    return (
      !!template &&
      (template.name !== option.title ||
        (template.description || '') !== (option.description || ''))
    );
  };

  const available = saved.filter(
    template => !options.some(option => option.templateId === template.id)
  );

  return (
    <>
      <VStack align="stretch" spacing={4}>
        <Text fontSize="xs" fontWeight="bold" color="gray.600" textAlign="left">
          CUSTOM OPTIONS
        </Text>

        {saved.length === 0 && options.length === 0 && (
          <Text fontSize="xs" color="gray.400">
            No custom options
          </Text>
        )}

        {options.map(option => (
          <Box key={option.id}>
            <VStack align="stretch" spacing={2}>
              <HStack align="flex-start" spacing={3}>
                <Input
                  flex={2}
                  value={option.title}
                  onChange={e => update(option.id, { title: e.target.value })}
                  onBlur={saveEdits}
                  placeholder="Enter an option..."
                  isDisabled={isDisabled}
                />
                <VStack flex={1} align="stretch" spacing={1}>
                  <NumberInput
                    value={option.amount ?? ''}
                    onChange={(_, value) =>
                      update(option.id, {
                        amount: Number.isNaN(value) ? '' : value,
                      })
                    }
                    min={0}
                    precision={2}
                    isDisabled={isDisabled}
                  >
                    <NumberInputField placeholder="0.00" onBlur={saveEdits} />
                  </NumberInput>
                  <Button
                    size="xs"
                    variant="link"
                    colorScheme="red"
                    alignSelf="flex-start"
                    onClick={() =>
                      change(options.filter(o => o.id !== option.id))
                    }
                    isDisabled={isDisabled}
                  >
                    Remove
                  </Button>
                </VStack>
              </HStack>
              <AutoGrowTextarea
                value={option.description}
                onChange={e =>
                  update(option.id, { description: e.target.value })
                }
                onBlur={saveEdits}
                placeholder="Enter a description..."
                isDisabled={isDisabled}
              />
              {isEdited(option) && (
                <HStack justify="space-between" spacing={3}>
                  <Text fontSize="xs" color="orange.600">
                    Edited
                  </Text>
                  <Button
                    size="xs"
                    variant="link"
                    colorScheme="blue"
                    onClick={() => {
                      const template = templateFor(option);
                      change(
                        options.map(o =>
                          o.id === option.id
                            ? {
                                ...o,
                                title: template.name,
                                description: template.description,
                              }
                            : o
                        )
                      );
                    }}
                    isDisabled={isDisabled}
                  >
                    Use default
                  </Button>
                </HStack>
              )}
            </VStack>
          </Box>
        ))}

        {options.length < MAX_OPTIONS && available.length > 0 && (
          <Select
            size="sm"
            maxW="280px"
            placeholder="Add an option…"
            value=""
            onChange={e => add(e.target.value)}
            isDisabled={isDisabled}
          >
            {available.map(template => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </Select>
        )}
      </VStack>
      <Divider />
    </>
  );
};

export default CustomPricingOptions;
